import { type ChangeEvent, memo, useCallback, useMemo, useState } from 'react';

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Checkbox,
  Chip,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Image } from '@realadvisor/image';
import { differenceInWeeks, formatDistanceToNowStrict } from 'date-fns';
import { EllipsisVerticalIcon, HouseIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { UserBuyerLeadKanbanItemFragment } from '@graphql/__generated__/graphql';
import { BuyerLeadItemMenu } from '@modules/_shared/components/buyer-lead/BuyerLeadKanbanItem';
import { BuyerLeadNextStep } from '@modules/_shared/components/buyer-lead/BuyerLeadNextStep';
import { useKanbanSelectedState } from '@modules/_shared/components/kanban/KanbanBoard';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { getStageCardColors } from '@modules/_shared/utils/leadStageSelectHelpers';
import { getListingPrices } from '@modules/_shared/utils/propertyTemplateFunctions';
import { useLocale } from '@shared/hooks/locale';

export const USER_BUYER_LEAD_KANBAN_ITEM_FRAGMENT = gql(/* GraphQL */ `
  fragment UserBuyerLeadKanbanItem on buyer_leads {
    id
    stage_id
    order_nr
    user_order_nr
    lead_stage {
      id
      status
    }
    created_at
    updated_at
    next_activity: activities(
      where: {
        done: { _eq: false }
        activity_type: { _in: [call, visit, task, assignment] }
      }
      order_by: { consolidated_due_at: asc_nulls_last }
      limit: 1
    ) {
      ...BuyerLeadDashboardActivity
    }
    lot {
      id
      title
      ...ListingPrice
      property {
        id
        route
        street_number
        postcode
        locality
        property_images(order_by: { order_nr: asc }, limit: 1) {
          id
          image {
            id
            url
          }
        }
      }
    }
  }
`);

export const UserBuyerLeadKanbanItem = memo(
  ({
    lead,
    onDelete,
  }: {
    lead: UserBuyerLeadKanbanItemFragment;
    onDelete?: (leadId: string) => void;
  }) => {
    const theme = useTheme();
    const palette = (theme.vars ?? theme).palette;
    const { selectedItems, setSelectedItems, forceSelectionMode } =
      useKanbanSelectedState();
    const background = useMemo(
      () => getStageCardColors(lead.lead_stage?.status, palette).background,
      [lead.lead_stage?.status, palette],
    );

    const selected = useMemo(
      () => selectedItems?.includes(lead.id) ?? false,
      [selectedItems, lead.id],
    );
    const memoizedOnSelect = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        if (setSelectedItems == null || selectedItems == null) {
          return;
        }

        if (event.target.checked) {
          setSelectedItems([...new Set([...selectedItems, lead.id])]);
        } else {
          setSelectedItems(selectedItems.filter(id => id !== lead.id));
        }
      },
      [lead.id, selectedItems, setSelectedItems],
    );

    return (
      <UserBuyerLeadKanbanItemContent
        onSelect={memoizedOnSelect}
        forceSelectionMode={forceSelectionMode}
        selected={selected}
        background={background}
        lead={lead}
        onDelete={onDelete}
      />
    );
  },
);

const UserBuyerLeadKanbanItemContent = memo<{
  background: string;
  lead: UserBuyerLeadKanbanItemFragment;
  forceSelectionMode: boolean;
  selected: boolean;
  onSelect: (event: ChangeEvent<HTMLInputElement>) => void;
  onDelete?: (leadId: string) => void;
}>(({ background, lead, forceSelectionMode, selected, onSelect, onDelete }) => {
  const { t, locale } = useLocale();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const prices = getListingPrices(lead.lot, t, locale);

  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      event.preventDefault();
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl],
  );
  const userActions = useMemo(
    () => (
      <IconButton size="small" onClick={handleOpenMenu} data-no-dnd>
        <EllipsisVerticalIcon size={18} />
      </IconButton>
    ),
    [handleOpenMenu],
  );
  const chipColors = useMemo(() => {
    const units = ['year', 'month', 'week', 'day', 'hour', 'minute'];
    const dateToCompare = new Date(lead.created_at);
    const distanceUnit = units.find(unit => {
      const delta =
        unit === 'week'
          ? differenceInWeeks(new Date(), dateToCompare, {
              roundingMethod: 'round',
            })
          : formatDistanceToNowStrict(dateToCompare, {
              unit: unit as 'year' | 'month' | 'day' | 'hour' | 'minute',
              roundingMethod: 'floor',
            }).match(/^\d+/)?.[0];

      return delta != null && Number(delta) !== 0;
    });

    const colorTable = {
      year: '#e53935',
      month: '#FB8C00',
      week: '#FDD835',
      day: '#039BE5',
      hour: '#4CAF50',
      minute: '#4CAF50',
    };

    return {
      backgroundColor:
        distanceUnit != null
          ? colorTable[distanceUnit as keyof typeof colorTable]
          : '#4CAF50',
      color: distanceUnit === 'week' ? 'neutral.main' : '#fff',
    };
  }, [lead.created_at]);
  const onCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  return (
    <Card
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        background,
        borderRadius: '8px',
      }}
    >
      <CardMedia
        sx={{
          width: 72,
          minWidth: 72,
          position: 'relative',
          overflow: 'hidden',
          borderTopLeftRadius: '8px',
          borderBottomLeftRadius: '8px',
          ['&:hover .buyer-lead-item-checkbox']: {
            visibility: 'visible !important',
            opacity: '1 !important',
          },
        }}
      >
        {lead.lot.property?.property_images &&
        lead.lot.property?.property_images.length > 0 ? (
          <Image
            src={lead.lot.property?.property_images[0]?.image.url ?? ''}
            options={{ w: 100, h: 100, f: 'jpg', c: 'fill' }}
          />
        ) : (
          <Box
            sx={theme => ({
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              background: (theme.vars ?? theme).palette.grey[200],
            })}
          >
            <HouseIcon size={32} color={'var(--mui-palette-grey-400)'} />
          </Box>
        )}
        <Box
          className="buyer-lead-item-checkbox"
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
            zIndex: 1,
            transition: 'visibility 0.2s, opacity 0.2s',
            visibility:
              selected || forceSelectionMode
                ? 'visible !important'
                : 'hidden !important',
            opacity:
              selected || forceSelectionMode ? '1 !important' : '0 !important',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.7)',
            '.dark &': {
              background: 'rgba(8, 10, 14, 0.6)',
            },
          }}
        >
          <Checkbox
            onChange={onSelect}
            disableFocusRipple
            disableTouchRipple
            disableRipple
            value={lead.id}
            checked={selected}
            sx={{ p: 3 }}
            onClick={e => {
              e.stopPropagation();
            }}
          />
        </Box>
      </CardMedia>
      <CardContent
        sx={{
          flexGrow: 1,
          minWidth: 0,
          py: '6px',
          pr: 0,
          pl: 1,
        }}
      >
        <Stack direction="column">
          <Typography variant="body2" noWrap sx={{ lineHeight: 1.2 }}>
            {lead.lot.title != null && lead.lot.title !== ''
              ? lead.lot.title
              : '-'}
          </Typography>
          {formatAddress(lead.lot.property, null).map((addrPart, index) => (
            <Typography
              variant="caption"
              noWrap
              key={index}
              sx={theme => ({ ...theme.text.ellipsis, lineHeight: 1.2 })}
            >
              {addrPart}
            </Typography>
          ))}

          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              mr: '-22px',
            }}
          >
            <Typography
              variant="caption"
              noWrap
              sx={{ fontWeight: 700, lineHeight: 1 }}
            >
              {prices.primary != null ? prices.primary : t('On request')}
            </Typography>
            <Chip
              label={
                <Typography
                  variant="caption"
                  sx={{ lineHeight: 1, position: 'relative', zIndex: 1 }}
                >
                  <TimeAgo
                    dateString={lead.created_at}
                    tooltipPlacement="bottom"
                    shortVersion={true}
                  />
                </Typography>
              }
              size="small"
              sx={{
                height: '19px',
                ...chipColors,
              }}
            />
          </Stack>
          <BuyerLeadNextStep activity={lead.next_activity[0]} />
        </Stack>
      </CardContent>
      <CardHeader action={userActions} sx={{ p: 1, pl: 0 }} />
      <BuyerLeadItemMenu
        anchorEl={anchorEl}
        leadStatus={lead.lead_stage?.status}
        leadId={lead.id}
        onCloseMenu={onCloseMenu}
        onDelete={onDelete}
      />
    </Card>
  );
});
